import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  CAMPAIGN_JOIN_MODES,
  CAMPAIGN_TRIGGERS,
  invalidateCampaignQueries,
} from '#/entities/campaign'
import type { CampaignJoinMode } from '#/entities/campaign'
import {
  TRIGGER_EVENTS,
  fromApiCampaignScope,
  toApiCampaignScope,
  triggerFromEvent,
} from '#/features/campaign-enrol'
import type {
  CampaignEnrolmentSeed,
  UseCampaignEnrolmentResult,
} from '#/features/campaign-enrol'
import { CampaignEnrollmentMode } from '#/shared/api/generated/communication/models'
import type {
  CampaignRead,
  CampaignUpdate,
} from '#/shared/api/generated/communication/models'
import {
  useGetCampaignApiV1CampaignsCampaignIdGet,
  useUpdateCampaignApiV1CampaignsCampaignIdPatch,
} from '#/shared/api/generated/communication/theCargoCommunication'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

const JOIN_MODES: Record<CampaignEnrollmentMode, CampaignJoinMode> = {
  [CampaignEnrollmentMode.manual]: CAMPAIGN_JOIN_MODES.manual,
  [CampaignEnrollmentMode.filter]: CAMPAIGN_JOIN_MODES.filter,
  [CampaignEnrollmentMode.trigger]: CAMPAIGN_JOIN_MODES.trigger,
}

/** The saved campaign, as the dialog's opening answers. */
export interface CampaignAudienceSeed {
  /** How people join. `PATCH /campaigns/{id}` has no `enrollment_mode`, so
   *  the editor works inside the saved one rather than offering a switch. */
  join: CampaignJoinMode
  enrolment: CampaignEnrolmentSeed
  filterDefinition: Record<string, unknown>
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value != null && !Array.isArray(value)
}

function toAudienceSeed(campaign: CampaignRead): CampaignAudienceSeed {
  return {
    join: JOIN_MODES[campaign.enrollment_mode],
    enrolment: {
      scope: fromApiCampaignScope(campaign.scope),
      userIds: campaign.applies_to_user_ids ?? [],
      trigger:
        triggerFromEvent(campaign.trigger_event) ??
        CAMPAIGN_TRIGGERS.whenQuoted,
      providerIds: campaign.provider_ids ?? [],
    },
    filterDefinition: isRecord(campaign.filter_definition)
      ? campaign.filter_definition
      : {},
  }
}

/**
 * What the edited answers add up to on `PATCH /campaigns/{id}`. Empty id
 * lists are sent as such — on update they mean "everyone" / "every source",
 * which is how a "pick" is turned back into an "all".
 */
export function toAudienceUpdate(
  join: CampaignJoinMode,
  enrolment: UseCampaignEnrolmentResult,
  filterDefinition: Record<string, unknown>,
): CampaignUpdate {
  const body: CampaignUpdate = {
    scope: toApiCampaignScope(enrolment.scope),
    applies_to_user_ids: enrolment.appliesToUserIds,
    provider_ids: enrolment.selectedProviderIds,
  }
  if (
    join === CAMPAIGN_JOIN_MODES.trigger &&
    enrolment.trigger !== CAMPAIGN_TRIGGERS.noOrder60d
  ) {
    body.trigger_event = TRIGGER_EVENTS[enrolment.trigger]
  }
  if (join === CAMPAIGN_JOIN_MODES.filter) {
    body.filter_definition = filterDefinition
  }
  return body
}

/**
 * The strategy card's Edit: read the campaign's saved audience while the
 * dialog is open, write the new one back. `GET /{id}` is the same read the
 * Sequence tab keeps, so opening the editor from there costs nothing.
 */
export function useCampaignAudienceEdit(
  campaignId: string,
  open: boolean,
  onSaved: () => void,
) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const query = useGetCampaignApiV1CampaignsCampaignIdGet(campaignId, {
    query: { enabled: open },
  })
  const campaign = query.data?.data

  const update = useUpdateCampaignApiV1CampaignsCampaignIdPatch({
    mutation: {
      onSuccess: () => {
        toast.success(t('campaigns.modal.edit.toasts.saved'))
        invalidateCampaignQueries(queryClient)
        onSaved()
      },
      onError: (error) =>
        toast.error(
          getApiErrorDetail(error) ?? t('campaigns.detail.actionError'),
        ),
    },
  })

  const onSave = useCallback(
    (body: CampaignUpdate) => update.mutate({ campaignId, data: body }),
    [update, campaignId],
  )

  return {
    seed: campaign == null ? null : toAudienceSeed(campaign),
    isLoading: query.isPending,
    onSave,
    isSaving: update.isPending,
  }
}
